<template>
  <div class="colors-container">
    <LazyLoadWhenInViewPort>
      <div
        :class="['colors', { 'align-center': swatchesAlignment === 'center' }]"
        :style="[`background: url(${swatchUrl}) 0 transparent;
      background-repeat: no-repeat; background-size: 0;`]"
      >
        <fieldset
          ref="ratingFieldset"
          role="radiogroup"
          aria-label="Color Swatches"
          style="background: inherit"
          :class="{ 'swatch-reduced-margin': remainingColorCount }"
        >
          <div
            v-for="(color) in colors"
            :key="color?.id"
            style="background: inherit"
            class="swatch-container"
            :class="{
              selected: selectedColor?.id === color?.id,
              'swatch-hover': hoverColor?.id === color?.id,
            }"
            type="radio"
            role="radio"
            tabindex="0"
            :aria-checked="true"
            @mouseover="mouseAction(color, true)"
            @mouseleave="mouseAction(selectedColor, false)"
            @click="selectedColor = color"
            @focus="mouseAction(color, true)"
            @blur="mouseAction(selectedColor, false)"
            @keydown.enter="selectedColor = color"
          >
            <label
              :id="color?.id"
              :for="`color-${color?.name}`"
              style="background: inherit; display: flex"
              :title="`${color?.name}`"
              :name="`color-${randomId}`"
              :aria-label="`color-${color?.name}`"
              :value="color?.id"
            >
              <span
                :name="color?.name"
                class="colorSwatch"
                :style="[`background-position-x: ${color?.offset}%;`]"
              />
            </label>
          </div>
        </fieldset>
        <div
          v-if="remainingColorCount"
          class="more-colors small"
        >
          <span v-if="clickAction !== 'disabled'">
            <a
              :href="productUrl"
              :target="openInNewTabRemainingColors ? '_blank' : '_self'"
              :rel="openInNewTabRemainingColors ? 'noopener' : null"
              aria-label="show more colors"
              @click="remainingColors"
            >
              +{{ remainingColorCount }}
            </a>
          </span>
          <span
            v-else
            :tabindex="-1"
          >+{{ remainingColorCount }}</span>
        </div>
      </div>
    </LazyLoadWhenInViewPort>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import generateRandomId from '../../../utils/randomNumber';
import buildColorSwatchSprite from '../../../composables/useImgSprite';
import LazyLoadWhenInViewPort from '../../LazyLoadWhenInViewPort';

export default {
  name: 'ColorSwatches',
  components: {
    LazyLoadWhenInViewPort,
  },
  props: {
    traits: {
      type: Object,
      default: () => { },
    },
    urlTemplate: {
      type: String,
      default: '',
    },
    productUrl: {
      type: String,
      default: '',
    },
    clickAction: {
      type: String,
      default: 'linkToPdp',
    },
    swatchesAlignment: {
      type: String,
      required: false,
      default: 'left',
    },
    openInNewTabRemainingColors: {
      type: Boolean,
      required: false,
      default: false,
    },
    colorSwatchEnhancement: {
      type: Boolean,
      required: false,
      default: false,
    },
    deviceType: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['color', 'remainingColors', 'isHovering', 'colorSwatch'],
  setup(props, { emit }) {
    // if there is no selectedColor use first from the colorMap
    const selectedColor = ref(props.traits?.colors?.selectedColor || props.traits?.colors?.colorMap[0]);
    const hoverColor = ref(props.traits?.colors?.selectedColor || props.traits?.colors?.colorMap[0]);

    const isMobile = props?.deviceType === 'PHONE';
    const MAX_COLORS = props?.colorSwatchEnhancement && isMobile ? 3 : 5;
    const randomId = generateRandomId();
    const colorMap = computed(() => {
      let colors = [];
      // if selected color is not first one in colorMap array then selected color is moved to first element.
      const actualSelectedColor = props.traits?.colors?.selectedColor || props.traits?.colors?.colorMap[0];
      const isNotFirst = actualSelectedColor?.id !== props.traits?.colors?.colorMap[0]?.id;
      if (isNotFirst) {
        colors = props.traits.colors?.colorMap.filter((color) => color.id !== actualSelectedColor?.id);
        colors.unshift(actualSelectedColor);
      }

      colors = colors.length === 0 ? [...props.traits.colors?.colorMap] : colors;
      colors.length = colors.length > MAX_COLORS ? MAX_COLORS : colors.length;
      return colors;
    });
    const remainingColorCount = computed(() => {
      let remainingColors = null;

      const colorMapLength = props.traits?.totalNumOfColors || props.traits?.colors?.colorMap.length;
      remainingColors = colorMapLength > MAX_COLORS
        ? colorMapLength - MAX_COLORS
        : null;
      return remainingColors;
    });
    const { swatchUrl, colors } = buildColorSwatchSprite(colorMap.value);
    emit('colorSwatch', {
      colors, swatchUrl, selectedColor, initial: true,
    });
    return {
      swatchUrl,
      colors,
      selectedColor,
      hoverColor,
      randomId,
      remainingColorCount,
    };
  },
  watch: {
    selectedColor(newSelectedColor) {
      this.$emit('color', newSelectedColor.id);
      this.$emit('colorSwatch', {
        colors: this.colors, swatchUrl: this.swatchUrl, selectedColor: newSelectedColor, initial: false,
      });
    },
    hoverColor(newSelectedColor) {
      this.$emit('color', newSelectedColor.id);
    },
  },
  methods: {
    remainingColors(event) {
      this.$emit('remainingColors', event);
    },
    mouseAction(newColor, state) {
      this.$emit('isHovering', { state, color: newColor?.id });
      this.hoverColor = newColor;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~settingSCSS";

.colors-container {
  overflow: hidden;
  height: 29px;

  .colors {
    margin: rem-calc(4 0);
    height: rem-calc(21);
    display: flex;
    align-items: center;
    fieldset {
      border: none;
      outline: none;
      display: flex;
      // position: fixed;
      padding: rem-calc(0);
    }
    input[type="radio"] {
      display: block;
      opacity: 0;
      position: absolute;
      height: rem-calc(17);
      width: rem-calc(17);
      cursor: pointer;
    }
    .swatch-container {
      height: rem-calc(19);
      width: rem-calc(19);
      margin-right: rem-calc(8);
      display: flex;
      align-items: center;
      justify-content: center;

          .colorSwatch {
            cursor: pointer;
            background-image: inherit;
            width: rem-calc(14);
            height: rem-calc(14);
            background-color: transparent;
            background-size: cover;
            border: 1px solid $gray-2-color;
          }
    }

    div.selected {
      .colorSwatch {
          outline: 1px solid $black;
          outline-offset: 1.5px;
      }
    }

    :deep(.picture-image).unavailable {
      opacity: .1;
    }

    .swatch-hover {
      border: $gray-5-color;
    }

    .more-colors {
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        display: block;
      }

      a {
        text-decoration: none;
        cursor: pointer;
      }
    }

    @include breakpoint(large up) {
      div.colors-container {
        display: block;
      }
    }
  }
}

.color-swatch-exp{
   margin-top: 8px;
  .colors{
    margin: 0;
    height: auto;

     fieldset {
      margin-left: 0;
    }
  }
}
</style>
<style lang="scss" scoped brand="bcom">
.colors-container {
  .colors {
  // Fix for small screen sizes only - PLP1-868
  @include breakpoint(390px down) {
      .swatch-reduced-margin {
        display: flex;
        width: 100%;

        .swatch-container {
          margin-right: auto;
        }
      }
    }
  }
}

.color-swatch-exp{
  height: rem-calc(26px);

  .colors{
    .swatch-container {
      height: rem-calc(26px);
      width: rem-calc(26px);
      margin-right: 2px;

        .colorSwatch {
          width: rem-calc(20px);
          height: rem-calc(20px);
        }
    }

    div.selected {
      .colorSwatch {
          outline: 1.5px solid $black;
      }
    }

    .more-colors{
        margin-left: 1px;
    }

    @include breakpoint(medium down) {
      fieldset, .swatch-reduced-margin {
        width: auto;

        .swatch-container {
           margin-right: 2px;
        }
      }
    }
  }
}
</style>
<style lang="scss" scoped brand="mcom">
.colors-container {
  .colors {
    input[type="radio"] {
      height: rem-calc(16);
      width: rem-calc(16);
    }

    .swatch-container {
      border-radius: $radio-border-radius;
      height: rem-calc(21);
      width: rem-calc(21);
        .colorSwatch {
          width: 1rem;
          height: 1rem;
        }
    }
    span{
      border-radius: $radio-border-radius;
    }
    div.selected {
      .colorSwatch {
          outline-offset: 2px;
      }
    }
  // Fix for small screen sizes only - PLP1-868
    @include breakpoint(410px down) {
      .swatch-reduced-margin {
        display: flex;
        width: 100%;

        .swatch-container {
          margin-right: auto;
        }
      }
    }
  }

  .swatch-hover:not(.selected) {
    border: 1px solid $gray-3-color;
  }
}

.color-swatch-exp{
  height: rem-calc(32px);

  .colors{
    .swatch-container {
      height: rem-calc(32px);
      width: rem-calc(32px);
      margin-right: 0;

        .colorSwatch {
          width: rem-calc(24px);
          height: rem-calc(24px);
        }
    }

    div.selected {
      .colorSwatch {
          outline: 1.5px solid $black;
          outline-offset: 2.5px;
      }
    }

    .more-colors{
        margin-left: 2px;
    }

    @include breakpoint(medium down) {
      fieldset, .swatch-reduced-margin {
        width: auto;
      }
    }
  }
}
</style>
